/**
 * Ny versjon av appen.
 *
 * HER LAA EN STILLE BLINDVEI. `registerType: "prompt"` i vite.config.ts er et
 * bevisst valg: en app som bytter versjon midt i loggingen er verre enn en
 * gammel versjon. Men ingenting i appen SPURTE. `injectRegister: "auto"` la
 * inn et skript som bare registrerte service workeren, og en «prompt» uten
 * spoersmaal betyr at den nye versjonen installerer seg og staar i «waiting»
 * for alltid. Appen paa hjemskjermen hentet derfor aldri inn noe nytt, uansett
 * hvor mange ganger den ble publisert — den eneste veien ut var aa slette
 * appen og legge den til paa nytt.
 *
 * Nytt oppsett: service workeren registreres herfra, og naar en ny versjon
 * staar klar blir det en RAD I MENYEN. Ingen automatisk omlasting, ingen toast
 * som stjeler fokus midt i et skift — regi tar den naar hen vil.
 *
 * Skrevet mot `navigator.serviceWorker` direkte og ikke mot
 * `virtual:pwa-register`, som trekker inn `workbox-window` for aa gjoere
 * nettopp dette. Den genererte `sw.js` har alt lytteren for `SKIP_WAITING`,
 * saa pakken ville vaert en avhengighet for tjue linjer.
 */

/** Hendelsen flatene lytter paa. Sendt naar en ny versjon staar og venter. */
const HENDELSE = "infoflyt:nyversjon";

/** Den nedlastede versjonen, eller null. */
let klar: ServiceWorker | null = null;

export function nyVersjonVenter(): boolean {
  return klar !== null;
}

/** Abonnerer paa at en ny versjon melder seg. Returnerer oppsigelsen. */
export function paaNyVersjon(lytter: () => void): () => void {
  window.addEventListener(HENDELSE, lytter);
  return () => window.removeEventListener(HENDELSE, lytter);
}

/**
 * Tar den nye versjonen i bruk. Appen lastes om.
 *
 * Kalles bare fra et trykk. Loggen ligger i OPFS og beroeres ikke av at
 * appskallet byttes — det er derfor dette er trygt midt i en produksjon, saa
 * lenge regi selv velger oeyeblikket.
 */
export function taINyVersjon(): void {
  const sw = klar;
  if (sw === null) return;
  /*
   * Omlastingen henger paa `controllerchange` og ikke paa en timer.
   * Service workeren maa ha tatt over foer siden lastes om; skjer det i
   * motsatt rekkefoelge, laster vi den gamle versjonen én gang til.
   */
  navigator.serviceWorker.addEventListener(
    "controllerchange",
    () => { window.location.reload(); },
    { once: true },
  );
  sw.postMessage({ type: "SKIP_WAITING" });
}

function meld(sw: ServiceWorker | null): void {
  /*
   * `controller === null` betyr FOERSTE gang appen aapnes.
   *
   * Da gaar en service worker gjennom «installed» helt normalt, og det er
   * ikke en oppdatering. Uten denne vakten sto «Ny versjon klar» i menyen
   * med én gang, paa en app som nettopp var lagt til hjemskjermen.
   */
  if (sw === null || navigator.serviceWorker.controller === null) return;
  klar = sw;
  window.dispatchEvent(new CustomEvent(HENDELSE));
}

/**
 * Registrerer service workeren og holder oeye med nye versjoner.
 *
 * Kalles én gang, fra inngangspunktet. Gjoer ingenting naar nettleseren ikke
 * har service workers — appen virker som foer, bare uten offline-skall.
 */
export function startOppdateringsvakt(): void {
  if (!("serviceWorker" in navigator)) return;

  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).then((reg) => {
      // Lastet ned mens appen var lukket.
      meld(reg.waiting);

      reg.addEventListener("updatefound", () => {
        const ny = reg.installing;
        if (ny === null) return;
        ny.addEventListener("statechange", () => {
          if (ny.state === "installed") meld(ny);
        });
      });

      /*
       * Ser etter ny versjon naar appen hentes fram.
       *
       * AA SE ETTER ER IKKE AA OPPDATERE. En installert iOS-webapp blir
       * sjelden lastet helt om — den legges bort og hentes fram — saa uten
       * dette kunne det gaa dager foer den oppdaget at noe nytt fanns.
       * Byttet skjer fortsatt bare naar regi trykker.
       */
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible" && klar === null) {
          void reg.update().catch(() => { /* uten nett. Da staar vi. */ });
        }
      });
    }).catch(() => {
      // Registreringen feilet. Appen virker, men uten offline-skall.
    });
  });
}
